/* eslint-disable */

/**
 * Weight Ladder (rv-weight-ladder).
 *
 * Presentation layer for the multi-quantity selector rendered by
 * snippets/rv-weight-ladder.liquid. It owns the ball sizing, the LB/KG toggle,
 * the goal filters and the selection summary.
 *
 * It deliberately does NOT own the cart. Every variant has a real
 * `.qty-input__input` inside a <quantity-input>, and the whole selector sits
 * inside <multi-purchase>, so totalling and /cart/add.js stay in
 * multi-purchase.js. This element only reads and writes those inputs.
 *
 * Three sync points keep the UI honest, because a quantity can change in three
 * different ways:
 * - `change` on an input covers a ball click or a typed quantity.
 * - `click` on a `.qty-input__btn` covers the steppers. quantity-input.js
 *   suppresses its own `change` when its cached quantity has gone stale (which
 *   it does as soon as this element writes a value), so the click is watched
 *   directly rather than trusted to bubble a change.
 * - The add-to-cart button gaining `is-success` means multi-purchase.js has
 *   just zeroed every input without announcing it.
 */
if (!customElements.get('rv-weight-ladder')) {
  /* Ball diameter in px at the smallest and largest weight on the ladder. The
     px value is then capped against the cell width in cqw (see the CSS), so a
     narrow product column shrinks the whole ladder proportionally: `cell` is
     the share of the cell the largest ball is allowed to fill. */
  const BALL = { min: 46, max: 88, cell: 92 };
  const DOT = { min: 14, max: 27 };
  const PAIR = { min: 13, max: 25 };
  const LB_TO_KG = 0.45359237;

  class RvWeightLadder extends HTMLElement {
    connectedCallback() {
      this.rungs = Array.from(this.querySelectorAll('[data-rung]'));
      this.rows = Array.from(this.querySelectorAll('[data-row]'));
      this.chips = Array.from(this.querySelectorAll('[data-chip]'));
      this.unitButtons = Array.from(this.querySelectorAll('[data-unit-btn]'));
      this.emptyState = this.querySelector('[data-empty]');
      this.countLabel = this.querySelector('[data-count-label]');
      this.totalEl = this.querySelector('[data-formatted-total]');
      this.atc = this.querySelector('[data-add-to-cart]');
      this.recommended = this.querySelector('[data-recommended]');
      this.trio = this.querySelector('[data-trio]');

      if (!this.rungs.length) return;

      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.unit = this.dataset.unit === 'kg' ? 'kg' : 'lb';
      this.range = null;

      this.emptyCountLabel = this.countLabel ? this.countLabel.textContent.trim() : '';
      this.emptyAtcLabel = this.atc ? this.atc.textContent.trim() : '';

      this.inputsByVariant = new Map();
      this.rows.forEach((row) => {
        const input = row.querySelector('.qty-input__input');
        if (input) this.inputsByVariant.set(row.dataset.variantId, input);
      });

      this.sizeBalls();
      this.addListeners();
      // setUnit() ends in a render, which paints the initial empty state.
      this.setUnit(this.unit);
    }

    addListeners() {
      this.rungs.forEach((rung) => {
        rung.addEventListener('click', () => this.add(rung.dataset.variantId, 1));
      });

      this.unitButtons.forEach((button) => {
        button.addEventListener('click', () => this.setUnit(button.dataset.unitBtn));
      });

      this.chips.forEach((chip) => {
        chip.addEventListener('click', () => this.toggleChip(chip));
      });

      if (this.recommended) {
        this.recommended.addEventListener('click', () => {
          this.setAll((this.recommended.dataset.recommended || '').split(',').filter(Boolean));
        });
      }

      this.addEventListener('change', (evt) => {
        if (evt.target.classList && evt.target.classList.contains('qty-input__input')) this.render();
      });

      this.addEventListener('click', (evt) => {
        if (evt.target.closest && evt.target.closest('.qty-input__btn')) this.render();
      });

      if (this.atc) {
        this.successObserver = new MutationObserver(() => {
          if (this.atc.classList.contains('is-success')) this.render();
        });
        this.successObserver.observe(this.atc, { attributes: true, attributeFilter: ['class'] });
      }
    }

    disconnectedCallback() {
      if (this.successObserver) this.successObserver.disconnect();
    }

    /**
     * Normalised 0-1 position of a weight on the ladder, by cube root so that
     * the balls scale by volume rather than by radius — a 50 lb ball reads as
     * heavier than a 5 lb one without being ten times as wide.
     * @param {number} weight - Weight in pounds.
     * @returns {number}
     */
    position(weight) {
      if (this.maxWeight === this.minWeight) return 1;
      const root = Math.cbrt(weight) - Math.cbrt(this.minWeight);
      const span = Math.cbrt(this.maxWeight) - Math.cbrt(this.minWeight);
      return Math.min(1, Math.max(0, root / span));
    }

    /**
     * @param {number} weight - Weight in pounds.
     * @param {object} scale - Object with `min` and `max` px bounds.
     * @returns {number} Diameter in px.
     */
    diameter(weight, scale) {
      return Math.round(scale.min + this.position(weight) * (scale.max - scale.min));
    }

    sizeBalls() {
      const weights = this.rungs.map((rung) => Number(rung.dataset.lb) || 0);
      this.minWeight = Math.min.apply(null, weights);
      this.maxWeight = Math.max.apply(null, weights);

      /* The track every ball stands on is the largest ball's own size, so the
         CSS can bottom-align them without knowing the scale. */
      this.style.setProperty('--rvwl-track-d', BALL.max + 'px');
      this.style.setProperty('--rvwl-track-dq', BALL.cell + 'cqw');

      this.rungs.forEach((rung) => {
        const ball = rung.querySelector('.rvwl__ball');
        if (!ball) return;

        const d = this.diameter(Number(rung.dataset.lb) || 0, BALL);
        /* The largest ball fills `cell`% of its column; every other ball keeps
           the same ratio to it, so the ladder stays proportional when capped. */
        const cq = (d / BALL.max) * BALL.cell;

        ball.style.setProperty('--rvwl-d', d + 'px');
        ball.style.setProperty('--rvwl-dq', cq.toFixed(2) + 'cqw');
        ball.style.setProperty('--rvwl-fn', Math.max(12, Math.round(d * 0.3)) + 'px');
        ball.style.setProperty('--rvwl-fnq', (cq * 0.3).toFixed(2) + 'cqw');
        ball.style.setProperty('--rvwl-fu', Math.max(7, Math.round(d * 0.145)) + 'px');
        ball.style.setProperty('--rvwl-fuq', (cq * 0.145).toFixed(2) + 'cqw');
      });

      this.querySelectorAll('[data-dot]').forEach((dot) => {
        const d = this.diameter(Number(dot.dataset.lb) || 0, DOT);
        dot.style.width = d + 'px';
        dot.style.height = d + 'px';
      });

      this.querySelectorAll('.rvwl__pair-balls i').forEach((mini) => {
        const d = this.diameter(Number(mini.dataset.lb) || 0, PAIR);
        mini.style.width = d + 'px';
        mini.style.height = d + 'px';
      });
    }

    /**
     * @param {number} weight - Weight in pounds.
     * @returns {number} Weight in the currently displayed unit.
     */
    inUnit(weight) {
      return this.unit === 'kg' ? Math.round(weight * LB_TO_KG) : weight;
    }

    setUnit(unit) {
      this.unit = unit === 'kg' ? 'kg' : 'lb';
      const label = this.unit.toUpperCase();

      this.unitButtons.forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.unitBtn === this.unit));
      });

      this.rungs.forEach((rung) => {
        const value = rung.querySelector('[data-weight-value]');
        const suffix = rung.querySelector('[data-weight-unit]');
        if (value) value.textContent = rung.dataset[this.unit];
        if (suffix) suffix.textContent = label;
      });

      this.rows.forEach((row) => {
        const name = row.querySelector('[data-weight-label]');
        const rung = this.rungs.find((r) => r.dataset.variantId === row.dataset.variantId);
        if (name && rung) name.textContent = rung.dataset[this.unit] + ' ' + label;
      });

      this.chips.forEach((chip) => {
        const range = chip.querySelector('[data-chip-range]');
        if (!range) return;
        const min = this.inUnit(Number(chip.dataset.min) || 0);
        const max = this.inUnit(Number(chip.dataset.max) || 0);
        range.textContent = min + '–' + max + ' ' + this.unit;
      });

      /* Liquid renders the recommended set's weights in pounds; the list is
         rebuilt here so it reads "7, 6 & 9 KG" rather than staying in LB while
         the balls above it switch. */
      if (this.trio) {
        const shown = (this.trio.dataset.trio || '')
          .split(',')
          .filter(Boolean)
          .map((weight) => this.inUnit(Number(weight) || 0));

        if (shown.length) {
          const list =
            shown.length > 1
              ? shown.slice(0, -1).join(', ') + ' & ' + shown[shown.length - 1]
              : String(shown[0]);
          this.trio.textContent = list + ' ' + label;
        }
      }

      this.render();
    }

    toggleChip(chip) {
      const wasPressed = chip.getAttribute('aria-pressed') === 'true';
      this.chips.forEach((other) => other.setAttribute('aria-pressed', 'false'));

      if (wasPressed) {
        this.range = null;
      } else {
        chip.setAttribute('aria-pressed', 'true');
        this.range = [Number(chip.dataset.min) || 0, Number(chip.dataset.max) || 0];
      }

      this.render();
    }

    /**
     * @param {string} variantId - Variant ID.
     * @param {number} delta - Amount to add.
     */
    add(variantId, delta) {
      const input = this.inputsByVariant.get(variantId);
      if (!input) return;
      this.write(input, (Number(input.value) || 0) + delta);
    }

    /**
     * Replaces the whole selection with one of each of the given variants.
     * @param {string[]} variantIds - Variant IDs.
     */
    setAll(variantIds) {
      this.inputsByVariant.forEach((input, id) => {
        this.write(input, variantIds.indexOf(id) === -1 ? 0 : 1);
      });
    }

    /**
     * @param {HTMLInputElement} input - Quantity input.
     * @param {number} value - New quantity.
     */
    write(input, value) {
      const next = Math.max(0, value);
      if (String(next) === input.value) return;
      input.value = String(next);
      /* multi-purchase.js listens for `change` on each input to keep its own
         total and cart payload up to date. */
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    render() {
      let count = 0;
      let total = 0;

      this.rows.forEach((row) => {
        const input = this.inputsByVariant.get(row.dataset.variantId);
        if (!input) return;

        const qty = Number(input.value) || 0;
        const line = (Number(input.dataset.price) || 0) * qty;

        count += qty;
        total += line;

        row.hidden = qty === 0;
        const lineEl = row.querySelector('[data-line]');
        if (lineEl) lineEl.textContent = this.formatMoney(line);
      });

      this.rungs.forEach((rung) => {
        const input = this.inputsByVariant.get(rung.dataset.variantId);
        const qty = input ? Number(input.value) || 0 : 0;
        const weight = Number(rung.dataset.lb) || 0;

        rung.setAttribute('aria-pressed', qty > 0 ? 'true' : 'false');
        rung.classList.toggle(
          'rvwl__rung--muted',
          Boolean(this.range) && (weight < this.range[0] || weight > this.range[1])
        );
        this.renderBadge(rung, qty);
        this.renderRungLabel(rung, qty);
      });

      if (this.emptyState) this.emptyState.hidden = count > 0;
      if (this.totalEl) this.totalEl.textContent = this.formatMoney(total);

      if (this.countLabel) {
        const template = count === 1 ? this.dataset.labelOne : this.dataset.labelOther;
        this.countLabel.textContent =
          count === 0 || !template ? this.emptyCountLabel : template.replace('[count]', count);
      }

      if (this.atc) {
        this.atc.disabled = count === 0;
        const template = this.dataset.atcTemplate;
        this.atc.textContent =
          count === 0 || !template ? this.emptyAtcLabel : template.replace('[count]', count);
      }
    }

    /**
     * @param {HTMLElement} rung - Ladder button.
     * @param {number} qty - Selected quantity.
     */
    renderBadge(rung, qty) {
      let badge = rung.querySelector('.rvwl__badge');

      if (qty < 2) {
        if (badge) badge.remove();
        return;
      }

      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'rvwl__badge';
        badge.setAttribute('aria-hidden', 'true');
        /* On the ball, not the track: the track is taller than the ball for
           every weight but the heaviest, so anchoring to it would leave the
           badge floating above the smaller balls. */
        const ball = rung.querySelector('.rvwl__ball');
        if (!ball) return;
        ball.appendChild(badge);
      }

      badge.textContent = qty;
    }

    /**
     * @param {HTMLElement} rung - Ladder button.
     * @param {number} qty - Selected quantity.
     */
    renderRungLabel(rung, qty) {
      const weight = rung.dataset[this.unit] + ' ' + this.unit.toUpperCase();
      const price = this.formatMoney(Number(rung.dataset.price) || 0);

      if (rung.disabled) {
        rung.setAttribute('aria-label', weight + ', sold out');
        return;
      }

      rung.setAttribute(
        'aria-label',
        weight + ', ' + price + (qty > 0 ? ', ' + qty + ' selected' : '')
      );
    }

    /**
     * Formats cents with the shop's own money format, so the ladder matches
     * the price shown everywhere else on the PDP.
     * @param {number} cents - Amount in cents.
     * @returns {string}
     */
    formatMoney(cents) {
      const amount = Number(cents) || 0;

      return this.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, name) => {
        switch (name) {
          case 'amount':
            return RvWeightLadder.withDelimiters(amount, 2, ',', '.');
          case 'amount_no_decimals':
            return RvWeightLadder.withDelimiters(amount, 0, ',', '.');
          case 'amount_with_comma_separator':
            return RvWeightLadder.withDelimiters(amount, 2, '.', ',');
          case 'amount_no_decimals_with_comma_separator':
            return RvWeightLadder.withDelimiters(amount, 0, '.', ',');
          case 'amount_with_apostrophe_separator':
            return RvWeightLadder.withDelimiters(amount, 2, "'", '.');
          default:
            return match;
        }
      });
    }

    /**
     * @param {number} cents - Amount in cents.
     * @param {number} precision - Decimal places.
     * @param {string} thousands - Thousands separator.
     * @param {string} decimal - Decimal separator.
     * @returns {string}
     */
    static withDelimiters(cents, precision, thousands, decimal) {
      const value = (cents / 100).toFixed(precision);
      const parts = value.split('.');
      const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return parts[1] ? whole + decimal + parts[1] : whole;
    }
  }

  customElements.define('rv-weight-ladder', RvWeightLadder);
}
